// Created: 2026-03-04 by Douglas
// Global agent notification hook — listens for agent_commands completions
// across all screens. Shows toast when response arrives while user is away.
// Mount this at the App level so it persists across navigation.
import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const AGENT_NAMES = {
  marty: 'Marty',
  gatsby: 'Gatsby',
  parker: 'Parker',
  harvey: 'Harvey',
  cardone: 'Cardone',
  buddha: 'Buddha',
  gadget: 'Gadget',
  sherlock: 'Sherlock',
};

const AGENT_COLORS = {
  marty: '#4ade80',
  gatsby: '#c084fc',
  parker: '#60a5fa',
  harvey: '#fbbf24',
  cardone: '#22d3ee',
  buddha: '#fb923c',
  gadget: '#f472b6',
  sherlock: '#f87171',
};

/**
 * Tracks pending agent_commands and shows toast notifications
 * when responses arrive while the user is on a different screen.
 *
 * Usage: Call useAgentNotifications() once in a component inside <Router>
 * (e.g., in OdvCentral or the main app shell).
 */
export function useAgentNotifications() {
  const location = useLocation();
  const navigate = useNavigate();
  const channelRef = useRef(null);
  const pendingIdsRef = useRef(new Set());

  // Track a new pending command (called by mailroom or chat bubble)
  const trackCommand = useCallback((commandId) => {
    pendingIdsRef.current.add(commandId);
  }, []);

  // Remove tracking (called when user sees the response in-context)
  const untrackCommand = useCallback((commandId) => {
    pendingIdsRef.current.delete(commandId);
  }, []);

  useEffect(() => {
    // Subscribe to all agent_commands completions for this session
    channelRef.current = supabase.channel('global-agent-notifications')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'agent_commands',
      }, (payload) => {
        const record = payload.new;
        if (!record) return;

        // Only care about commands we're tracking
        if (!pendingIdsRef.current.has(record.id)) return;

        // Only fire on terminal states
        if (record.status !== 'completed' && record.status !== 'failed') return;

        // Remove from tracking
        pendingIdsRef.current.delete(record.id);

        // If user is already on the mailroom screen, don't toast (they'll see it in-context)
        if (location.pathname.includes('/mailroom')) return;

        // Show toast notification
        const agentName = record.response_data?.agent_name || record.target_agent || record.intent || 'Agent';
        const displayName = AGENT_NAMES[agentName?.toLowerCase()] || agentName;
        const color = AGENT_COLORS[agentName?.toLowerCase()] || '#aea36d';
        const isError = record.status === 'failed';
        const subject = record.context?.subject;
        const truncatedSubject = subject?.length > 40 ? subject.substring(0, 40) + '...' : subject;

        toast(
          (t) => (
            <div
              className="flex items-start gap-3 cursor-pointer max-w-sm"
              onClick={() => {
                toast.dismiss(t.id);
                navigate('/mailroom');
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold"
                style={{ backgroundColor: `${color}20`, color, border: `1.5px solid ${color}40` }}
              >
                {displayName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-100">
                  {isError ? 'Agent failed' : `${displayName} responded`}
                </p>
                {truncatedSubject && (
                  <p className="text-xs text-gray-400 truncate mt-0.5">
                    Re: {truncatedSubject}
                  </p>
                )}
                <p className="text-[10px] text-gray-500 mt-1">
                  Tap to view in Mail Room
                </p>
              </div>
            </div>
          ),
          {
            duration: 8000,
            position: 'top-right',
            style: {
              background: '#1a1a2e',
              border: `1px solid ${isError ? '#f8717140' : color + '40'}`,
              borderRadius: '12px',
              padding: '12px 16px',
              maxWidth: '380px',
            },
          }
        );
      })
      .subscribe();

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [location.pathname, navigate]);

  return { trackCommand, untrackCommand };
}
